"use client"

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Search, Users, Loader2 } from "lucide-react"
import { vehicleRouteService } from "../../services/vehicleRouteService"
import toast from "react-hot-toast"
import { getFullName } from "../../utils/urlUtils"

export default function StudentAssignmentModal({ 
  isOpen, 
  onClose, 
  stop, 
  shiftId, 
  routeId, 
  onStudentsUpdated,
  availableStudents 
}) {
  const [selectedStudents, setSelectedStudents] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [loading, setLoading] = useState(false)
  const [currentShiftType, setCurrentShiftType] = useState(null)
  const [overrideStudents, setOverrideStudents] = useState([])

  useEffect(() => {
    if (isOpen && stop) {
      // Extract IDs if stop.students contains objects
      const stopStudentIds = (stop.students || []).map(s => typeof s === 'string' ? s : s._id);
      const availableIds = availableStudents.map(s => s._id);
      const validStudentIds = stopStudentIds.filter(id => availableIds.includes(id));
      setSelectedStudents(validStudentIds);
    }
  }, [isOpen, stop, availableStudents]);


  // Get current shift type from the stop object
  useEffect(() => {
    if (isOpen && stop) {
      setCurrentShiftType(stop.shiftType || 'pickup');
    }
  }, [isOpen, stop]);

  const filteredStudents = availableStudents.filter(student => {
    if (!searchTerm || searchTerm.trim() === '') {
      return true; // Show all students if search is empty
    }
    
    const searchLower = searchTerm.toLowerCase().trim();
    const fullName = getFullName(student).toLowerCase();
    const firstName = student.firstName?.toLowerCase() || '';
    const middleName = student.middleName?.toLowerCase() || '';
    const lastName = student.lastName?.toLowerCase() || '';
    const email = student.email?.toLowerCase() || '';
    const admissionNumber = student.admissionNumber ? String(student.admissionNumber).toLowerCase() : '';
    
    return (
      fullName.includes(searchLower) ||
      firstName.includes(searchLower) ||
      middleName.includes(searchLower) ||
      lastName.includes(searchLower) ||
      email.includes(searchLower) ||
      admissionNumber.includes(searchLower)
    );
  })

  const handleStudentToggle = (studentId) => {
    setSelectedStudents(prev => {
      if (prev.includes(studentId)) {
        return prev.filter(id => id !== studentId)
      } else {
        return [...prev, studentId]
      }
    })
    setOverrideStudents(prev => prev.filter(id => id !== studentId)) // Remove from override if toggled off
  }

  // Handle override (force assign) for a student
  const handleOverride = (studentId) => {
    if (!selectedStudents.includes(studentId)) {
      setSelectedStudents(prev => [...prev, studentId])
    }
    setOverrideStudents(prev => prev.includes(studentId) ? prev : [...prev, studentId])
  }

  const handleSave = async () => {
    try {
      setLoading(true)
      
      if (routeId) {
        await vehicleRouteService.updateStop(routeId, shiftId, stop._id, {
          students: selectedStudents,
          override: overrideStudents.length > 0,
          overrideStudents: overrideStudents
        })
      }
      
      onStudentsUpdated(selectedStudents)
      toast.success("Student assignments updated successfully")
      onClose()
    } catch (error) {
      console.error("Error updating student assignments:", error)
      
      if (error.response?.data?.message && error.response?.data?.message.includes("already assigned to another")) {
        const conflictingNames = error.response?.data?.conflictingStudentNames || [];
        const shiftType = error.response?.data?.shiftType || 'shift';
        
        toast.error(`The following students are already assigned to another ${shiftType}: ${conflictingNames.join(", ")}. Please select different students.`, {
          duration: 5000,
        });
      } else {
        toast.error(error.response?.data?.message || "Failed to update student assignments", {
          duration: 5000,
        });
      }
    } finally {
      setLoading(false)
    }
  }

  const getStudentName = (studentId) => {
    const student = availableStudents.find(s => s._id === studentId)
    if (!student) {
      console.warn(`[StudentAssignmentModal] Unknown StudentId in selectedStudents:`, studentId);
    }
    return student ? getFullName(student) : "Unknown Student"
  }

  const getStudentStatus = (student) => {
    if (student.assignments && student.assignments.length > 0) {
      const pickupAssignments = student.pickupAssignments || [];
      const dropAssignments = student.dropAssignments || [];
      
      if (currentShiftType === 'pickup' && pickupAssignments.length > 0) {
        const assignment = pickupAssignments[0];
        const vehicleNumber = assignment.vehicleNumber || 'N/A';
        return {
          status: 'conflict',
          message: `Already in pickup (${vehicleNumber})`,
          vehicleNumber: vehicleNumber
        };
      } else if (currentShiftType === 'drop' && dropAssignments.length > 0) {
        const assignment = dropAssignments[0];
        const vehicleNumber = assignment.vehicleNumber || 'N/A';
        return {
          status: 'conflict',
          message: `Already in drop (${vehicleNumber})`,
          vehicleNumber: vehicleNumber
        };
      } else if (currentShiftType === 'pickup' && dropAssignments.length > 0) {
        const vehicleNumber = dropAssignments[0].vehicleNumber || 'N/A';
        return {
          status: 'available',
          message: `Available for pickup (has drop assignment)`,
          vehicleNumber: vehicleNumber
        };
      } else if (currentShiftType === 'drop' && pickupAssignments.length > 0) {
        const vehicleNumber = pickupAssignments[0].vehicleNumber || 'N/A';
        return {
          status: 'available',
          message: `Available for drop (has pickup assignment)`,
          vehicleNumber: vehicleNumber
        };
      }
    }
    
    return {
      status: 'available',
      message: 'Available for assignment'
    };
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Manage Students - {stop?.name}
          </DialogTitle>
          <DialogDescription>
            Select students to assign to this {currentShiftType} stop. Each student can be in one pickup and one drop.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col gap-4">
          {/* Info about assignment rules */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <div className="text-blue-600 mt-0.5">
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="text-sm text-blue-800">
                <p className="font-medium">Assignment Rules</p>
                <p className="text-xs mt-1">
                  • Students can be in one pickup and one drop<br/>
                  • Students already in another {currentShiftType} cannot be selected<br/>
                  • Students with {currentShiftType === 'pickup' ? 'drop' : 'pickup'} assignments are available
                </p>
              </div>
            </div>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by first name, middle name, last name, email, or admission number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Selected Students */}
          {selectedStudents.length > 0 && (
            <div className="space-y-2">
              <Label className="text-sm font-medium">Selected Students ({selectedStudents.length})</Label>
              <div className="flex flex-wrap gap-2 max-h-20 overflow-y-auto">
                {selectedStudents.map(studentId => (
                  <Badge 
                    key={studentId} 
                    variant="secondary"
                    className="gap-1 bg-[#0263cc]/10 text-[#0263cc]"
                  >
                    <Users className="h-3 w-3" />
                    {getStudentName(studentId)}
                    <button
                      onClick={() => handleStudentToggle(studentId)}
                      className="ml-1 hover:bg-[#0263cc]/20 rounded-full p-0.5"
                    >
                      ×
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Students List */}
          <div className="flex-1 overflow-y-auto border rounded-lg p-4">
            <div className="space-y-2">
              {filteredStudents.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No students found</p>
                </div>
              ) : (
                filteredStudents.map((student) => {
                  const status = getStudentStatus(student);
                  const isDisabled = status.status === 'conflict' && !overrideStudents.includes(student._id);
                  const isSelected = selectedStudents.includes(student._id);
                  
                  return (
                    <div
                      key={student._id}
                      className={`flex items-center space-x-3 p-3 rounded-lg border ${
                        isDisabled 
                          ? 'bg-gray-100 opacity-60' 
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => handleStudentToggle(student._id)}
                        disabled={isDisabled}
                      />
                      <div className="flex-1">
                        <div className="font-medium">{getFullName(student)}</div>
                        <div className="text-sm text-gray-500">
                          {student.email} • Grade {student.grade} {student.section}
                          {student.admissionNumber && ` • Admission: ${student.admissionNumber}`}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {isSelected && (
                          <Badge variant="secondary" className="bg-[#0263cc]/10 text-[#0263cc]">
                            Selected
                          </Badge>
                        )}
                        {status.status === 'conflict' && (
                          <Badge 
                            variant="outline" 
                            className="text-red-600 border-red-300 cursor-help flex items-center gap-1"
                          >
                            {status.message}
                            <button
                              type="button"
                              className="ml-1 text-red-600 hover:text-red-800 font-bold text-lg leading-none"
                              title="Override and assign here"
                              onClick={() => handleOverride(student._id)}
                            >
                              ×
                            </button>
                          </Badge>
                        )}
                        {status.status === 'available' && status.vehicleNumber && (
                          <Badge 
                            variant="outline" 
                            className="text-green-600 border-green-300"
                          >
                            {status.message}
                          </Badge>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={loading}
            className="bg-[#0263cc] hover:bg-[#0263cc]/90"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {loading ? "Saving..." : "Save Assignments"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
} 